import React, { useState } from 'react';
import axios from 'axios';
import { X, Mail, KeyRound, Eye, EyeOff, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ForgotPasswordModal
 * accentColor: Tailwind ring/border color like 'blue' | 'green' | 'purple'
 */
const ForgotPasswordModal = ({ onClose, accentColor = 'blue' }) => {
  const [step, setStep] = useState(1); // 1: email, 2: code+password, 3: success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const ring = {
    blue:   'focus:ring-blue-400',
    green:  'focus:ring-green-400',
    purple: 'focus:ring-purple-400',
  }[accentColor] || 'focus:ring-blue-400';

  const btn = {
    blue:   'linear-gradient(135deg, #1D4ED8, #2563EB)',
    green:  'linear-gradient(135deg, #166534, #16A34A)',
    purple: 'linear-gradient(135deg, #4A1D96, #7C3AED)',
  }[accentColor] || 'linear-gradient(135deg, #1D4ED8, #2563EB)';

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.detail || 'E-posta gönderilemedi. Adres doğru mu?');
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError('Şifre en az 6 karakter olmalı.'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { email, token: code, new_password: newPassword });
      setStep(3);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Kod yanlış veya süresi dolmuş.');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-7" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {step === 1 ? 'Şifremi Unuttum' : step === 2 ? 'Yeni Şifre Belirle' : 'Şifre Güncellendi'}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {step === 1 && 'E-postanıza sıfırlama kodu göndereceğiz'}
              {step === 2 && `${email} adresine gönderilen kodu girin`}
              {step === 3 && 'Şifreniz başarıyla güncellendi'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step 1: Email */}
        {step === 1 && (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">E-posta Adresi</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="hesabiniz@email.com"
                  data-testid="forgot-pw-email-input"
                  className={`w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 ${ring} focus:border-transparent transition`}
                />
              </div>
            </div>
            {error && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-pw-send-btn"
              className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all disabled:opacity-60"
              style={{ background: loading ? '#9CA3AF' : btn }}
            >
              {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Gönderiliyor...</span> : 'Sıfırlama Kodu Gönder'}
            </button>
          </form>
        )}

        {/* Step 2: Code + New Password */}
        {step === 2 && (
          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Doğrulama Kodu</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  required
                  maxLength={8}
                  placeholder="E-postanızdaki kodu girin"
                  data-testid="forgot-pw-code-input"
                  className={`w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 ${ring} focus:border-transparent transition font-mono tracking-widest`}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Yeni Şifre</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="En az 6 karakter"
                  data-testid="forgot-pw-new-password-input"
                  className={`w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 ${ring} focus:border-transparent transition pr-16`}
                />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => { setStep(1); setError(''); }}
                className="flex-1 py-3 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition">
                Geri
              </button>
              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-pw-reset-btn"
                className="flex-1 py-3 rounded-xl font-semibold text-white text-sm transition-all disabled:opacity-60"
                style={{ background: loading ? '#9CA3AF' : btn }}
              >
                {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Güncelleniyor...</span> : 'Şifreyi Güncelle'}
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="text-center py-4">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <p className="text-gray-700 font-medium mb-1">Şifreniz başarıyla güncellendi!</p>
            <p className="text-xs text-gray-400 mb-6">Yeni şifrenizle giriş yapabilirsiniz.</p>
            <button
              onClick={onClose}
              data-testid="forgot-pw-done-btn"
              className="w-full py-3 rounded-xl font-semibold text-white text-sm"
              style={{ background: btn }}
            >
              Giriş Sayfasına Dön
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
